import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Loader } from '../loader/loader.component';

const buttonVariants = cva(
  cn(
    'typo-primary-500 inline-flex items-center justify-center whitespace-nowrap rounded focus-visible:outline focus-visible:outline-primary px-3 py-2 h-10',
    '',
  ),
  {
    variants: {
      variant: {
        default:
          'bg-primary hover:bg-primary-hover text-white-text disabled:pointer-events-none disabled:bg-light-gray disabled:text-disabled-text disabled:border-none',
        secondary: 'bg-white border border-other-stroke text-dark',
        ghost: 'p-0',
        icon: 'p-0',
        topbar:
          'bg-white-text px-2 py-2 h-full border-x border-x-other-stroke rounded-none typo-primary-500 font-semibold disabled:transparent disabled:text-disabled-text px-4',
      },
      loading: {
        true: 'bg-light-gray pointer-events-none cursor-not-allowed text-secondary-text disabled:pointer-events-none disabled:bg-light-gray disabled:text-secondary-text',
        false: '',
      },
      size: {
        default: '',
        ghost: 'p-0',
        small: 'h-8 typo-secondary',
        'topbar-wide': 'min-w-[200px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  index?: number;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, variant, size, loading, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            loading,
            className,
          }),
        )}
        ref={ref}
        {...props}
      >
        {loading && <Loader className="w-5 h-5 [&>span]:border-y-secondary-text [&>span]:border-x-transparent" />}

        {!loading && children}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export default Button;
